import { LoadoutStats, SimulationInput, SimulationLoadoutStatsOutput, SimulationOutput, SimulationRunOptions } from './types';
import { Rolls } from './rolls';
import { Utils } from './utilities';
import { maybeYieldForSimulationChunk, throwIfSimulationAborted } from './simulation-control';

const EXECUTION_TIME = 'ExecutionTime';
let executionCounter = 0;

export async function runSimulation(input: Partial<SimulationInput> = {}, options: SimulationRunOptions = {}): Promise<SimulationOutput> {
    const executionLabel = `${EXECUTION_TIME}-${executionCounter++}`;
    console.time(executionLabel);

    try {
        throwIfSimulationAborted(options.signal);
        const sanitized = Utils.sanitizeInput(input);
        const outputs = await Promise.all(sanitized.loadouts.map((_, index) => simulate(sanitized, index, options)));
        throwIfSimulationAborted(options.signal);

        return {
            primordial: sanitized.primordial,
            loadoutOutputs: outputs,
        };
    } finally {
        console.timeEnd(executionLabel);
    }
}

/**
 * PRIVATE
 */

async function simulate(input: SimulationInput, index: number, options: SimulationRunOptions): Promise<SimulationLoadoutStatsOutput> {
    const powerResults: number[] = [];
    const loadout = input.loadouts[index];

    let numAnyHits = 0;
    let numFullHits = 0;
    let numFullMisses = 0;
    let wounds = 0;

    for (let i = 0; i < input.config.numberOfSimulations; i++) {
        const yieldPromise = maybeYieldForSimulationChunk(i + 1, options);
        if (yieldPromise) {
            await yieldPromise;
        }
        const aeonTrespassValue = getSimulationATValue(input.primordial);
        const hits = getHits(input, loadout);
        numAnyHits += hits > 0 ? 1 : 0;
        numFullHits += hits === loadout.attackDice ? 1 : 0;
        numFullMisses += hits === 0 ? 1 : 0;

        let powerValue = 0;
        if (hits > 0) {
            powerValue = getPower(input, loadout, hits, aeonTrespassValue);
            wounds += powerValue >= aeonTrespassValue ? 1 : 0;
        }
        powerResults.push(powerValue);
    }

    throwIfSimulationAborted(options.signal);
    const diceRolledOnFullHit = Utils.getPowerDiceColors(loadout, loadout.maxHits, input.config.disablePowerDiceLimits);

    return {
        loadout: loadout,
        chanceToHit: getPercent(numAnyHits, input.config.numberOfSimulations),
        chanceToFullHit: getPercent(numFullHits, input.config.numberOfSimulations),
        chanceToFullMiss: getPercent(numFullMisses, input.config.numberOfSimulations),
        chanceToWound: getPercent(wounds, input.config.numberOfSimulations),
        diceRolledOnFullHit: diceRolledOnFullHit,
        power: powerResults,
        powerCumulativeProbabilities: calculateCumulativeProbability(powerResults, input.config.numberOfSimulations),
    };
}

function calculateCumulativeProbability(powerResults: number[], numSimulations: number): number[] {
    const maxPowerValue = Math.max(...powerResults);
    const cumulativeProbability = new Array(maxPowerValue + 1).fill(0);
    const powerCounts = new Array(maxPowerValue + 1).fill(0);

    // Count the occurrences of each power value
    powerResults.forEach((power) => {
        powerCounts[power]++;
    });

    // Calculate cumulative probability (from highest power value to lowest)
    let cumulativeCount = 0;
    for (let i = maxPowerValue; i >= 0; i--) {
        cumulativeCount += powerCounts[i];
        cumulativeProbability[i] = getPercent(cumulativeCount, numSimulations);
    }

    // Ensure the first index (0) is always 100% since it's "at least 0 power"
    cumulativeProbability[0] = 100;

    return cumulativeProbability;
}

function getPercent(part: number, whole: number): number {
    if (whole === 0) return 0;
    let percent = Math.floor((part / whole) * 100);
    if (percent === 0 && part > 0) return 1;
    if (percent === 100 && part < whole) return 99;
    return percent;
}

function getHits(input: SimulationInput, loadout: LoadoutStats): number {
    return input.config.skipAttackRoll
        ? loadout.maxHits
        : Math.max(
              Rolls.executeAttackRoll({
                  kratosPool: input.kratosPool,
                  toHitValue: input.primordial.toHitValue,
                  stats: loadout,
                  disableReRolls: input.config.disableAttackReRolls,
              }),
              loadout.autoHits,
          );
}

function getPower(input: SimulationInput, loadout: LoadoutStats, hits: number, aeonTrespassValue: number): number {
    return Rolls.executePowerRoll({
        kratosPool: input.kratosPool,
        hits: Math.min(hits, loadout.maxHits),
        aeonTrespassValue,
        reRollThreshold: input.config.powerReRollThreshold,
        reRollStrategy: input.config.powerReRollStrategy,
        stats: loadout,
        disableReRolls: input.config.disablePowerReRolls,
        disableDiceLimits: input.config.disablePowerDiceLimits,
    });
}

function getSimulationATValue(primordial: SimulationInput['primordial']): number {
    const aeonTrespassValues = primordial.aeonTrespassValues;
    if (aeonTrespassValues && aeonTrespassValues.length > 0) {
        return aeonTrespassValues[Math.floor(Math.random() * aeonTrespassValues.length)];
    }

    return primordial.aeonTrespassValue;
}
